import type { ComponentKind, MutatingScreenKind, PropertyDescription } from "@glide/app-description";
import { makeColumnProperty, makeEnumProperty, makeStringProperty } from "@glide/app-description";
import { type TableColumn, getTableColumnDisplayName, isPrimitiveArrayType, isPrimitiveType } from "@glide/type-schema";
import { type InputOutputTables, makeEmptyComponentDescription } from "@glide/common-core/dist/js/description";
import { getDocURL } from "@glide/common-core/dist/js/docUrl";
import { ImagePickerSourceHint, ImageSizePreset } from "@glide/component-utils";
import {
    type AppDescriptionContext,
    type ComponentDescriptor,
    type EnumPropertyCase,
    type PropertyDescriptor,
    ColumnPropertyFlag,
    ColumnPropertyHandler,
    EnumPropertyHandler,
    PropertySection,
    imageProperties,
    isImageType,
    isRequiredPropertyHandler,
    getPrimitiveOrPrimitiveArrayNonHiddenColumnsSpec,
} from "@glide/function-utils";
import { assert } from "@glideapps/ts-necessities";
import { type WireRowComponentHydratorConstructor, WireComponentKind, type WireInflationBackend } from "@glide/wire";
import {
    doesMutatingScreenSupportIsRequired,
    labelCaptionStringOptions,
    makeCaptionStringPropertyDescriptor,
} from "./descriptor-utils";
import { type FilePickerComponentDescription, inflateFileOrImagePicker } from "./file-picker";
import { ComponentHandlerBase } from "./handler";
import { getFeatureSetting } from "@glide/common-core";

export const ComponentKindImagePicker: ComponentKind = "image-picker";

const propertyNamePropertyHandler = new ColumnPropertyHandler(
    "propertyName",
    "Column",
    [
        ColumnPropertyFlag.Required,
        ColumnPropertyFlag.Editable,
        ColumnPropertyFlag.EditedInApp,
        ColumnPropertyFlag.DefaultCaption,
        ColumnPropertyFlag.AllowUserProfileColumns,
    ],
    undefined,
    imageProperties,
    getPrimitiveOrPrimitiveArrayNonHiddenColumnsSpec,
    t => isImageType(t, false, true),
    PropertySection.Data
);

const allSources: EnumPropertyCase<ImagePickerSourceHint>[] = [
    {
        value: ImagePickerSourceHint.Any,
        label: "Camera and Photo Roll",
    },
    {
        value: ImagePickerSourceHint.CameraOnly,
        label: "Camera only",
    },
];

const cameraHintPropertyHandler = new EnumPropertyHandler(
    { cameraHint: ImagePickerSourceHint.Any },
    "Upload From",
    "Upload From",
    allSources,
    {
        name: "Upload From",
        order: 1000,
    },
    "radio"
);

const imageSizePropertyHandler = new EnumPropertyHandler(
    { imageSizePreset: ImageSizePreset.High },
    "Resolution",
    "Resolution",
    [
        { value: ImageSizePreset.Low, label: "Low" },
        { value: ImageSizePreset.High, label: "High" },
        { value: ImageSizePreset.Original, label: "Original" },
    ],
    PropertySection.Options,
    "dropdown",
    undefined,
    undefined,
    undefined,
    undefined,
    "Lower resolution saves file storage and reduces upload times. 1280x1280 for Low and 2560x2560 for High. GIFs will not be resized to preserve animations."
);

interface ImagePickerComponentDescription extends FilePickerComponentDescription {
    readonly cameraHint: PropertyDescription | undefined;
    readonly imageSizePreset?: PropertyDescription;
}

export class ImagePickerComponentHandler extends ComponentHandlerBase<ImagePickerComponentDescription> {
    constructor() {
        super(ComponentKindImagePicker);
    }

    public getIsEditor(): boolean {
        return true;
    }

    public needValidation(): boolean {
        return true;
    }

    public getDescriptor(
        desc: ImagePickerComponentDescription | undefined,
        _tables: InputOutputTables | undefined,
        _ccc: AppDescriptionContext,
        mutatingScreenKind: MutatingScreenKind | undefined
    ): ComponentDescriptor {
        const properties: PropertyDescriptor[] = [
            propertyNamePropertyHandler,
            makeCaptionStringPropertyDescriptor("Image", false, mutatingScreenKind, labelCaptionStringOptions),
        ];
        if (doesMutatingScreenSupportIsRequired(mutatingScreenKind, desc?.propertyName)) {
            properties.push(isRequiredPropertyHandler);
        }

        properties.push(cameraHintPropertyHandler);
        if (getFeatureSetting("clientSideImageResizing")) {
            properties.push(imageSizePropertyHandler);
        }

        properties.push(...this.getBasePropertyDescriptors());
        return {
            name: "Image Picker",
            description: "Upload an image",
            img: "co-image-picker",
            group: "Form Elements",
            helpUrl: getDocURL("imagePicker"),
            properties,
        };
    }

    public inflate(
        ib: WireInflationBackend,
        desc: ImagePickerComponentDescription
    ): WireRowComponentHydratorConstructor | undefined {
        const sourceHint = cameraHintPropertyHandler.getEnum(desc);
        const imageSizePreset = imageSizePropertyHandler.getEnum(desc);

        return inflateFileOrImagePicker(ib, desc, p => ({
            ...p,
            kind: WireComponentKind.ImagePicker,
            sourceHint,
            imageSizePreset,
        }));
    }

    public static defaultComponent(column: TableColumn): ImagePickerComponentDescription {
        assert(isPrimitiveType(column.type) || isPrimitiveArrayType(column.type));
        return {
            ...makeEmptyComponentDescription(ComponentKindImagePicker),
            propertyName: makeColumnProperty(column.name),
            defaultValue: undefined,
            caption: makeStringProperty(getTableColumnDisplayName(column)),
            visibilityFilters: [],
            cameraHint: makeEnumProperty(ImagePickerSourceHint.Any),
            imageSizePreset: makeEnumProperty(ImageSizePreset.High),
        };
    }
}
